import React, { useState, useEffect } from 'react';
import {
  Typography, Box, FormControl, InputLabel, Select, MenuItem, TextField,
  Button, CircularProgress, Alert, Card, CardContent, Divider,
  SelectChangeEvent
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Use Grid2 for MUI v5 if available, or ensure correct Grid import
import { getDepositAddresses, submitDepositRequest } from '../utils/api';

// Explicitly define the DepositAddress interface
interface DepositAddress {
  currency: string;
  chain: string;
  address: string;
  qr_code?: string;
}

interface FormDataType {
  currency: string;
  chain: string;
  amount: string;
  screenshot: File | null;
}
interface DepositFormProps {
    clientId: number;
  }
  const DepositForm: React.FC<DepositFormProps> = ({ clientId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [chains, setChains] = useState<string[]>([]);
  const [depositAddresses, setDepositAddresses] = useState<DepositAddress[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<DepositAddress | null>(null);
  
  const [formData, setFormData] = useState<FormDataType>({
    currency: '',
    chain: '',
    amount: '',
    screenshot: null
  });
  
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCurrencyOptions = async () => {
      try {
        const response = await getDepositAddresses();
        if (response.data.success) {
          const addresses: DepositAddress[] = response.data.data;
          
          // Extract unique currencies
          const uniqueCurrencies = Array.from(
            new Set(addresses.map((addr: DepositAddress) => addr.currency))
          );
          setCurrencies(uniqueCurrencies);
          
          setDepositAddresses(addresses);
        }
      } catch (err) {
        setError('Failed to load deposit options');
        console.error(err);
      }
    };
    
    fetchCurrencyOptions();
  }, []);
  
  useEffect(() => {
    if (formData.currency) {
      const availableChains = depositAddresses
        .filter((addr: DepositAddress) => addr.currency === formData.currency)
        .map((addr: DepositAddress) => addr.chain);
      
      const uniqueChains = Array.from(new Set(availableChains));
      setChains(uniqueChains);
      
      setFormData(prev => ({ ...prev, chain: '' }));
    }
  }, [formData.currency, depositAddresses]);
  
  useEffect(() => {
    if (formData.currency && formData.chain) {
      const address = depositAddresses.find(
        (addr: DepositAddress) => addr.currency === formData.currency && addr.chain === formData.chain
      );
      setSelectedAddress(address || null);
    } else {
      setSelectedAddress(null);
    }
  }, [formData.currency, formData.chain, depositAddresses]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      setFormData(prev => ({ ...prev, screenshot: file }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (!formData.currency || !formData.chain || !formData.amount || !formData.screenshot) {
        throw new Error('Please fill in all fields');
      }
      
      if (!selectedAddress) {
        throw new Error('No deposit address available for selected options');
      }
      
      const data = new FormData();
      data.append('client_id', clientId.toString());
      data.append('amount', formData.amount);
      data.append('currency', formData.currency);
      data.append('chain', formData.chain);
      data.append('deposit_address', selectedAddress.address);
      data.append('screenshot', formData.screenshot);
      
      const response = await submitDepositRequest(data);
      
      if (response.data.success) {
        setSuccess('Deposit request submitted successfully!');
        setFormData({
          currency: '',
          chain: '',
          amount: '',
          screenshot: null
        });
        setScreenshotPreview(null);
      }
    } catch (err: any) {
      const errorMessage = 
        err.response?.data?.message || 
        err.message || 
        'Failed to submit deposit request';
      
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card sx={{ 
      borderRadius: 2, 
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
      overflow: 'visible'
    }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Deposit Funds
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }} component="div">
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={formData.currency}
                  onChange={handleSelectChange}
                  label="Currency"
                >
                  {currencies.map(currency => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }} component="div">
              <FormControl fullWidth disabled={!formData.currency}>
                <InputLabel>Network/Chain</InputLabel>
                <Select
                  name="chain"
                  value={formData.chain}
                  onChange={handleSelectChange}
                  label="Network/Chain"
                >
                  {chains.map(chain => (
                    <MenuItem key={chain} value={chain}>
                      {chain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12 }} component="div">
              <TextField
                fullWidth
                name="amount"
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: formData.currency ? formData.currency : null
                }}
              />
            </Grid>
            
            {selectedAddress && (
              <>
                <Grid size={{ xs: 12 }} component="div">
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Deposit Address
                  </Typography>
                  
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'background.paper', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        mb: 2
                      }}
                    >
                      {selectedAddress.address}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigator.clipboard.writeText(selectedAddress.address)}
                    >
                      Copy Address
                    </Button>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }} component="div">
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    QR Code
                  </Typography>
                  
                  {selectedAddress.qr_code ? (
                    <Box 
                      component="img"
                      src={selectedAddress.qr_code}
                      alt="QR Code"
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '200px',
                        display: 'block',
                        mx: 'auto'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No QR code available
                    </Typography>
                  )}
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }} component="div">
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Upload Payment Screenshot
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ height: '56px', mb: 2 }}
                  >
                    {formData.screenshot ? 'Change Screenshot' : 'Select Screenshot'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  
                  {screenshotPreview && (
                    <Box
                      component="img"
                      src={screenshotPreview}
                      alt="Payment Screenshot"
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '200px',
                        display: 'block',
                        mx: 'auto',
                        borderRadius: 1
                      }}
                    />
                  )}
                </Grid>
              </>
            )}
            
            <Grid size={{ xs: 12 }} component="div">
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !selectedAddress || !formData.screenshot}
                sx={{ mt: 2, py: 1.5, fontSize: '1rem' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Submit Deposit Request'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default DepositForm;